import { useState } from 'react'
import './sessionDetail.css'
import { MuscleFigure } from '../muscle/MuscleFigure'
import { muscleLabel, HIGHLIGHT_ALIAS } from '../../lib/muscles'
import { useUnits } from '../../lib/UnitsProvider'
import { TOKENS } from '../ui/dashboard-primitives'
const alias = (s: string) => HIGHLIGHT_ALIAS[s] ?? s
// The UI is English-default (see CLAUDE.md), so numbers format en-US regardless
// of browser locale — and all summary metrics use ONE formatter so weight and
// energy can't render in different locales. Rounded: kcal/kg show no decimals.
const fmtNum = (n: number) => Math.round(n).toLocaleString('en-US')

export function SessionSummaryHeader({ summary }: { summary: {
  totalLoad: number; energy: number; sets: number; primary: string[]; secondary: string[] } }) {
  const { tonnageParts } = useUnits()
  const [hover, setHover] = useState<string | null>(null)
  const chip = (slug: string, isPrimary: boolean) => {
    const active = hover === alias(slug)
    return (
      <span key={slug} onMouseEnter={() => setHover(alias(slug))} onMouseLeave={() => setHover(null)}
        className="inline-flex items-center gap-[.4rem] rounded-full font-mono uppercase"
        style={{ fontSize: 10, letterSpacing: '.14em', padding: '.28em .7em', whiteSpace: 'nowrap',
          // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
          background: isPrimary ? 'rgba(232,197,160,.12)' : TOKENS.creamHairline,
          // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
          border: `1px solid ${active ? TOKENS.amber : isPrimary ? 'rgba(232,197,160,.30)' : TOKENS.creamBorder}`,
          color: active ? TOKENS.amberLight : isPrimary ? TOKENS.amber : TOKENS.creamDim,
          outline: active ? `2px solid ${TOKENS.amber}` : 'none', outlineOffset: 1, fontWeight: 500,
          transition: 'outline-color .14s cubic-bezier(.23,1,.32,1), color .14s cubic-bezier(.23,1,.32,1), border-color .14s cubic-bezier(.23,1,.32,1)' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />{muscleLabel(slug)}
      </span>
    )
  }
  const figProps = { primary: summary.primary, secondary: summary.secondary, interactive: true,
    hoverSlug: hover, onRegionEnter: setHover, onRegionLeave: () => setHover(null) }
  // Fix round, Minor: was called twice (once per prop) below.
  const totalLoad = tonnageParts(summary.totalLoad)
  return (
    <div className="session-hero rounded-2xl" style={{ padding: '1.6rem 1.75rem',
      background: TOKENS.creamHairline, border: '1px solid ' + TOKENS.creamBorder,
      boxShadow: '0 24px 48px -20px rgba(20,14,12,.55)' }}>
      <div className="flex gap-2">
        <div className="flex flex-col items-center gap-[.4rem]">
          <MuscleFigure side="front" {...figProps} className="w-[84px] h-[168px]" />
          <span style={capStyle}>Front</span>
        </div>
        <div className="flex flex-col items-center gap-[.4rem]">
          <MuscleFigure side="back" {...figProps} className="w-[84px] h-[168px]" />
          <span style={capStyle}>Back</span>
        </div>
      </div>
      {/* A size container: the metrics scale with the width they actually get (cqi), and
          minmax(0,1fr) lets a cell shrink below its content. With repeat(3,1fr) the three
          cells (120 + 120 + 103px) could not fit a 285px column on a 375px phone and widened
          the page to 387px (measured). */}
      <div className="@container flex flex-col gap-5" style={{ minWidth: 0 }}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
          <Metric v={totalLoad.value} u={totalLoad.unit} label="Total load" accent first />
          <Metric v={fmtNum(summary.energy)} u="kcal" label="Energy" />
          <Metric v={fmtNum(summary.sets)} u="sets" label="Working sets" />
        </div>
        <div>
          <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '.28em', color: TOKENS.creamDim, marginBottom: '.55rem', fontWeight: 500 }}>Muscles trained</div>
          <div className="flex flex-wrap gap-[.4rem]">
            {summary.primary.map((s) => chip(s, true))}
            {summary.secondary.map((s) => chip(s, false))}
          </div>
        </div>
      </div>
    </div>
  )
}
const capStyle: React.CSSProperties = { fontFamily: "'Geist Mono',monospace", fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: TOKENS.creamFaint }
function Metric({ v, u, label, accent, first }: { v: string; u: string; label: string; accent?: boolean; first?: boolean }) {
  return (
    // Sizes follow the metrics' container (SessionSummaryHeader's @container): full size from a
    // ~410px row, and small enough at 287px (a 375px phone) for a six-character value like
    // "27,216lb". The unit is in em so it shrinks with its value (0.47em is the old .85rem/1.8rem).
    <div className="metric" style={{ padding: first ? '0 clamp(0.6rem, 4cqi, 1.15rem) 0 0' : '0 clamp(0.6rem, 4cqi, 1.15rem)' }}>
      <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 'clamp(1.125rem, 7cqi, 1.8rem)', fontWeight: 500, letterSpacing: '-.02em', lineHeight: 1, color: accent ? TOKENS.amber : TOKENS.cream, fontVariantNumeric: 'tabular-nums' }}>
        {v}<span style={{ fontSize: '0.47em', color: TOKENS.creamDim, marginLeft: '.18em', fontWeight: 400 }}>{u}</span>
      </div>
      <div style={{ fontFamily: "'Geist Mono',monospace", fontSize: 9.5, letterSpacing: '.2em', textTransform: 'uppercase', color: TOKENS.creamDim, marginTop: '.55rem' }}>{label}</div>
    </div>
  )
}
